import { ref, Ref } from 'vue';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { UserState } from '@/composables/User';
import type { FlowStructureDto } from '@/types/flow-structure';

export interface TargetCalculation {
  in: number | null;
  out_1?: number;
  out_2?: number;
  timeout_exceeded?: boolean;
}

interface FlowchartLink {
  fromOperator: string;
  fromConnector: string;
  fromSubConnector: number;
  toOperator: string;
  toConnector: string;
  toSubConnector: number;
}

export type TargetBulkState = Record<string, TargetCalculation | 'loading'>;

interface RequiredChannels {
  hasEmail: boolean;
  hasSms: boolean;
  hasPush: boolean;
  hasWhatsapp: boolean;
  hasRcs: boolean;
}

function detectSendingChannels(flow: FlowStructureDto): RequiredChannels {
  const result: RequiredChannels = {
    hasEmail: false, hasSms: false, hasPush: false, hasWhatsapp: false, hasRcs: false,
  };
  for (const step of flow.steps) {
    if (step.type === 'boxsendemail') {
      result.hasEmail = true;
    } else if (step.type === 'boxsendsms') {
      result.hasSms = true;
    } else if (step.type === 'boxsendpushnotification') {
      result.hasPush = true;
    } else if (step.type === 'boxsendwhatsapp') {
      result.hasWhatsapp = true;
    } else if (step.type === 'boxsendrcs') {
      result.hasRcs = true;
    }
  }
  return result;
}

function buildStepLevels(flow: FlowStructureDto): string[][] {
  const levels: string[][] = [];
  if (!flow.rootStepId) return levels;

  const stepsMap = new Map(flow.steps.map((s) => [s.id, s]));
  const processedSteps = new Set<string>();
  let currentLevel = [flow.rootStepId];

  while (currentLevel.length > 0) {
    levels.push(currentLevel);
    currentLevel.forEach((id) => processedSteps.add(id));

    const nextLevelSet = new Set<string>();
    for (const stepId of currentLevel) {
      const step = stepsMap.get(stepId);
      if (step?.children) {
        for (const child of step.children) {
          if (!processedSteps.has(child.id)) {
            const childStep = stepsMap.get(child.id);
            const parents = childStep?.parents || [];
            const allParentsProcessed = parents.every((p) => processedSteps.has(p.id));
            if (allParentsProcessed) {
              nextLevelSet.add(child.id);
            }
          }
        }
      }
    }
    currentLevel = Array.from(nextLevelSet);
  }

  return levels;
}

function parseStepParameters(parameters: Record<string, any>): Record<string, any> {
  const parsedParams: Record<string, any> = {};
  Object.keys(parameters || {}).forEach((key) => {
    const value = parameters[key];
    if (typeof value === 'string') {
      try {
        parsedParams[key] = JSON.parse(value);
      } catch {
        parsedParams[key] = value;
      }
    } else {
      parsedParams[key] = value;
    }
  });
  return parsedParams;
}

export function useTargetBulkCalculations() {
  const targetCalculations: Ref<TargetBulkState> = ref({});
  const isCalculating = ref(false);
  let isAborted = false;

  /**
   * Calculate target bulk for all steps in the flow (level by level)
   */
  const calculateTargetBulk = async (
    flow: FlowStructureDto,
    campaignId: number,
    commercialCampaign = false,
  ) => {
    if (!flow || !flow.steps.length) {
      targetCalculations.value = {};
      return;
    }

    const idShop = UserState.activeShop?.id ?? 0;
    if (!idShop) {
      targetCalculations.value = {};
      return;
    }

    // Set all steps to loading state
    const loadingState: TargetBulkState = {};
    flow.steps.forEach((step) => {
      loadingState[step.id] = 'loading';
    });
    targetCalculations.value = loadingState;
    isCalculating.value = true;
    isAborted = false;

    try {
      const levels = buildStepLevels(flow);
      const requiredChannels = detectSendingChannels(flow);
      const stepsMap = new Map(flow.steps.map((s) => [s.id, s]));
      let isFirstLevel = true;

      for (let levelIndex = 0; levelIndex < levels.length; levelIndex++) {
        const level = levels[levelIndex];
        if (isAborted) {
          break;
        }

        const stepsToCalculate = level.map((stepId) => {
          const step = stepsMap.get(stepId)!;
          const parsedParams = parseStepParameters(step.parameters || {});
          parsedParams.id = step.type;

          const parents = (step.parents || []).map((p) => ({
            operatorId: p.id,
            output: p.output,
          }));

          return {
            operatorId: stepId,
            operatorType: step.type,
            operatorConfig: parsedParams,
            parents: parents.length > 0 ? parents : undefined,
            hasChildren: (step.children?.length || 0) > 0,
          };
        });

        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/workflow/calculateBulkTargetStep',
          {},
          '',
          {
            idShop,
            campaignId,
            commercialCampaign,
            steps: stepsToCalculate,
            ...(isFirstLevel && { requiredChannels }),
          },
        );

        isFirstLevel = false;

        if (result.error || !result.success) {
          level.forEach((stepId) => {
            targetCalculations.value[stepId] = { in: null, out_1: undefined, error: result.error } as any;
          });
          break;
        }

        Object.assign(targetCalculations.value, result.data);
      }
    } catch (error) {
      const timeoutState: TargetBulkState = {};
      if (flow.rootStepId) {
        timeoutState[flow.rootStepId] = { in: null, timeout_exceeded: true };
      }
      targetCalculations.value = timeoutState;
    } finally {
      isCalculating.value = false;
    }
  };

  const abortCalculation = () => {
    isAborted = true;
  };

  /**
   * Reset calculations
   */
  const resetCalculations = () => {
    targetCalculations.value = {};
    isCalculating.value = false;
  };

  /**
   * Get calculation for a specific step
   */
  const getStepCalculation = (stepId: string): TargetCalculation | 'loading' | null => targetCalculations.value[stepId] ?? null;

  return {
    targetCalculations,
    isCalculating,
    calculateTargetBulk,
    resetCalculations,
    getStepCalculation,
    abortCalculation,
  };
}
